import {
  Box,
  Button,
  Divider,
  Flex,
  Input,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Text,
} from '@chakra-ui/react'
import { CervicalConsistency, CervicalPosition, MembraneStatus } from '@hb/shared'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useField } from 'react-final-form'

// ─── Bishop Score ────────────────────────────────────────────────────────────

/**
 * Standard Bishop Score (max 13).
 * ≥8 = favorable cervix; <6 = cervical ripening recommended.
 */
const calcBishopScore = (
  dilation: number,
  effacement: number,
  station: number,
  consistency: CervicalConsistency,
  position: CervicalPosition,
): number => {
  let score = 0

  // Dilation
  if (dilation >= 5) score += 3
  else if (dilation >= 3) score += 2
  else if (dilation >= 1) score += 1

  // Effacement
  if (effacement >= 80) score += 3
  else if (effacement >= 60) score += 2
  else if (effacement >= 40) score += 1

  // Station
  if (station >= 1) score += 3
  else if (station >= -1) score += 2
  else if (station >= -2) score += 1

  // Consistency
  if (consistency === CervicalConsistency.SOFT) score += 2
  else if (consistency === CervicalConsistency.MEDIUM) score += 1

  // Position
  if (position === CervicalPosition.ANTERIOR) score += 2
  else if (position === CervicalPosition.MID_POSITION) score += 1

  return score
}

const bishopLabel = (score: number) => {
  if (score >= 8) return { text: 'Favorable', color: '#48bb78' }
  if (score >= 6) return { text: 'Borderline', color: '#f6ad55' }
  return { text: 'Unfavorable', color: '#fc8181' }
}

// ─── Section Label ───────────────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="lg" fontFamily="hero-new" opacity={0.8} fontWeight={400}>
    {children}
  </Text>
)

// ─── Segmented Control ───────────────────────────────────────────────────────

function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { id: T; label: string; sub?: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <Flex gap={2} w="100%">
      {options.map(opt => {
        const isActive = value === opt.id
        return (
          <Button
            key={opt.id}
            flex={1}
            h={opt.sub ? '56px' : '48px'}
            variant={isActive ? 'solid' : 'outline'}
            colorScheme={isActive ? 'teal' : 'gray'}
            borderRadius={10}
            onClick={() => onChange(opt.id)}>
            <Flex flexFlow="column" align="center" gap={0}>
              <Text fontSize="sm" fontWeight={isActive ? 600 : 400} fontFamily="hero-new">
                {opt.label}
              </Text>
              {opt.sub ? (
                <Text fontSize="xs" opacity={0.7} fontFamily="hero-new">
                  {opt.sub}
                </Text>
              ) : null}
            </Flex>
          </Button>
        )
      })}
    </Flex>
  )
}

// ─── Dilation Input ──────────────────────────────────────────────────────────

/** SVG cervical os visual — circle that grows from 0 → 10 cm */
const DilationOs = ({ cm }: { cm: number }) => {
  const MAX_R = 52
  const r = (cm / 10) * MAX_R
  const cx = 64
  const cy = 64
  const fontSize = cm >= 5 ? '22' : '16'
  return (
    <Box w="128px" h="128px" flexShrink={0}>
      <svg width="128" height="128" viewBox="0 0 128 128">
        {/* Outer reference ring */}
        <circle cx={cx} cy={cy} r={MAX_R} fill="none" stroke="#CBD5E0" strokeWidth={2} />
        {/* Filled os */}
        {r > 1 ? (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="rgba(129,180,242,0.25)"
            stroke="#63B3ED"
            strokeWidth={2.5}
          />
        ) : (
          <circle cx={cx} cy={cy} r={3} fill="#CBD5E0" />
        )}
        {/* Label */}
        <text
          x={cx}
          y={cy + 5}
          textAnchor="middle"
          fontSize={fontSize}
          fontFamily="hero-new, sans-serif"
          fontWeight="600"
          fill={cm > 0 ? '#3182CE' : '#A0AEC0'}>
          {cm > 0 ? `${cm}` : '0'}
        </text>
      </svg>
    </Box>
  )
}

const dilationGradient = 'linear-gradient(to right, #bee3f8, #3182ce)'
const dilationGradientDark = 'linear-gradient(to right, #2a4a6b, #63b3ed)'

const DilationInput = () => {
  const { input } = useField<number>('dilation', { subscription: { value: true } })
  const [dragging, setDragging] = useState(false)
  const [dragVal, setDragVal] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const thumb = containerRef.current?.querySelector<HTMLElement>('[role="slider"]')
    if (thumb) thumb.tabIndex = -1
  })

  const displayVal = dragging ? dragVal : (input.value ?? 0)

  return (
    <Flex flexFlow="column" w="100%" gap={3}>
      <Flex justify="space-between" align="center">
        <SectionLabel>DILATION</SectionLabel>
        <Text fontSize="xl" fontWeight={600} fontFamily="hero-new" color="#3182CE">
          {displayVal} cm
        </Text>
      </Flex>
      <Flex align="center" gap={5} w="100%">
        <DilationOs cm={displayVal} />
        <Box ref={containerRef} flex={1} minW={0} overflow="visible" pb={6}>
          <Slider
            focusThumbOnChange={false}
            min={0}
            max={10}
            step={0.5}
            value={displayVal}
            onChangeStart={v => {
              setDragging(true)
              setDragVal(v)
            }}
            onChange={v => setDragVal(v)}
            onChangeEnd={v => {
              setDragging(false)
              input.onChange(v)
            }}>
            {[0, 2, 4, 6, 8, 10].map(mark => (
              <SliderMark
                key={mark}
                value={mark}
                mt={3}
                transform="translateX(-50%)"
                fontSize="xs"
                fontFamily="hero-new"
                color="gray.400">
                {mark}
              </SliderMark>
            ))}
            <SliderTrack
              h="20px"
              borderRadius="full"
              border="2px solid"
              borderColor="gray.200"
              bg={dilationGradient}
              _dark={{ bg: dilationGradientDark, borderColor: 'gray.600' }}>
              <SliderFilledTrack bg="whiteAlpha.400" />
            </SliderTrack>
            <SliderThumb h="32px" w="32px" borderRadius="full" boxShadow="md" />
          </Slider>
        </Box>
      </Flex>
    </Flex>
  )
}

// ─── Effacement Input ─────────────────────────────────────────────────────────

const effacementGradient = 'linear-gradient(to right, #c6f6d5, #276749)'
const effacementGradientDark = 'linear-gradient(to right, #1a3a28, #48bb78)'

const EffacementInput = () => {
  const { input } = useField<number>('effacement', { subscription: { value: true } })
  const [dragging, setDragging] = useState(false)
  const [dragVal, setDragVal] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const thumb = containerRef.current?.querySelector<HTMLElement>('[role="slider"]')
    if (thumb) thumb.tabIndex = -1
  })

  const displayVal = dragging ? dragVal : (input.value ?? 0)

  return (
    <Flex flexFlow="column" w="100%" gap={3}>
      <Flex justify="space-between" align="center">
        <SectionLabel>EFFACEMENT</SectionLabel>
        <Text
          fontSize="xl"
          fontWeight={600}
          fontFamily="hero-new"
          color="#276749"
          _dark={{ color: '#48bb78' }}>
          {displayVal}%
        </Text>
      </Flex>
      <Box ref={containerRef} w="100%" overflow="visible" pb={6}>
        <Slider
          focusThumbOnChange={false}
          min={0}
          max={100}
          step={5}
          value={displayVal}
          onChangeStart={v => {
            setDragging(true)
            setDragVal(v)
          }}
          onChange={v => setDragVal(v)}
          onChangeEnd={v => {
            setDragging(false)
            input.onChange(v)
          }}>
          {[0, 25, 50, 75, 100].map(mark => (
            <SliderMark
              key={mark}
              value={mark}
              mt={3}
              transform="translateX(-50%)"
              fontSize="xs"
              fontFamily="hero-new"
              color="gray.400">
              {mark}%
            </SliderMark>
          ))}
          <SliderTrack
            h="20px"
            borderRadius="full"
            border="2px solid"
            borderColor="gray.200"
            bg={effacementGradient}
            _dark={{ bg: effacementGradientDark, borderColor: 'gray.600' }}>
            <SliderFilledTrack bg="whiteAlpha.400" />
          </SliderTrack>
          <SliderThumb h="32px" w="32px" borderRadius="full" boxShadow="md" />
        </Slider>
      </Box>
    </Flex>
  )
}

// ─── Station Input ────────────────────────────────────────────────────────────

/**
 * Vertical slider for fetal station (−5 to +5).
 * Visual convention: top = negative (high in pelvis), bottom = positive (crowning).
 * Chakra's vertical slider increases bottom-to-top, so we invert the value.
 */
const StationInput = () => {
  const { input } = useField<number>('station', { subscription: { value: true } })
  const [dragging, setDragging] = useState(false)
  const [dragVal, setDragVal] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const thumb = containerRef.current?.querySelector<HTMLElement>('[role="slider"]')
    if (thumb) thumb.tabIndex = -1
  })

  const rawVal = input.value ?? 0
  // Invert for visual: slider bottom = +5, top = -5 → store inverted (-val)
  const sliderVal = -rawVal
  // During drag, dragVal is in slider-space (inverted); displayVal is in form-space
  const displayVal = dragging ? -dragVal : rawVal

  const stationColor =
    displayVal > 2 ? '#e53e3e' : displayVal > 0 ? '#ed8936' : displayVal < 0 ? '#4299e1' : '#48bb78'

  const stationLabel =
    displayVal <= -3
      ? 'Floating'
      : displayVal <= -1
        ? 'High'
        : displayVal === 0
          ? 'At Spines'
          : displayVal <= 2
            ? 'Low'
            : 'Crowning'

  return (
    <Flex flexFlow="column" w="100%" gap={3}>
      <Flex justify="space-between" align="center">
        <SectionLabel>STATION</SectionLabel>
        <Flex align="baseline" gap={2}>
          <Text fontSize="xl" fontWeight={600} fontFamily="hero-new" color={stationColor}>
            {displayVal > 0 ? `+${displayVal}` : displayVal}
          </Text>
          <Text fontSize="md" fontWeight={500} color={stationColor} fontFamily="hero-new">
            {stationLabel}
          </Text>
        </Flex>
      </Flex>
      <Flex align="stretch" gap={4} w="100%" minH="220px">
        {/* Vertical slider */}
        <Box ref={containerRef} display="flex" alignItems="center" justifyContent="center" w="60px">
          <Slider
            orientation="vertical"
            min={-5}
            max={5}
            step={1}
            focusThumbOnChange={false}
            value={dragging ? dragVal : sliderVal}
            h="200px"
            onChangeStart={v => {
              setDragging(true)
              setDragVal(v)
            }}
            onChange={v => setDragVal(v)}
            onChangeEnd={v => {
              setDragging(false)
              input.onChange(-v)
            }}>
            <SliderTrack
              w="20px"
              borderRadius="full"
              border="2px solid"
              borderColor="gray.200"
              bg="linear-gradient(to bottom, #4299e1, #48bb78, #e53e3e)"
              _dark={{ borderColor: 'gray.600' }}>
              <SliderFilledTrack bg="transparent" />
            </SliderTrack>
            <SliderThumb h="32px" w="32px" borderRadius="full" boxShadow="md" />
          </Slider>
        </Box>

        {/* Station reference diagram */}
        <Flex flex={1} flexFlow="column" justify="space-between" py={1}>
          {([-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5] as const).map(s => {
            const isActive = displayVal === s
            const isZero = s === 0
            return (
              <Flex
                key={s}
                align="center"
                gap={2}
                cursor="pointer"
                onClick={() => input.onChange(s)}>
                <Box
                  h={isZero ? '2px' : '1px'}
                  flex={1}
                  bg={isZero ? 'gray.500' : isActive ? stationColor : 'gray.200'}
                  _dark={{ bg: isZero ? 'gray.400' : isActive ? stationColor : 'gray.600' }}
                  transition="background 0.15s"
                />
                <Text
                  fontSize={isActive ? 'md' : 'sm'}
                  fontFamily="hero-new"
                  fontWeight={isActive || isZero ? 600 : 400}
                  color={isActive ? stationColor : isZero ? 'gray.500' : 'gray.400'}
                  w="28px"
                  textAlign="right">
                  {s > 0 ? `+${s}` : s}
                </Text>
                {isZero ? (
                  <Text fontSize="xs" color="gray.400" fontFamily="hero-new" w="52px">
                    spines
                  </Text>
                ) : (
                  <Box w="52px" />
                )}
              </Flex>
            )
          })}
        </Flex>
      </Flex>
    </Flex>
  )
}

// ─── Presenting Part ──────────────────────────────────────────────────────────

const PRESENTING_PARTS = ['Vertex', 'Breech', 'Face', 'Brow', 'Shoulder']

const PresentingPartInput = () => {
  const { input } = useField<string>('presentingPart', { subscription: { value: true } })
  const [showCustom, setShowCustom] = useState(
    !!input.value && !PRESENTING_PARTS.includes(input.value),
  )

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>PRESENTING PART</SectionLabel>
      <Flex gap={2} wrap="wrap" w="100%">
        {PRESENTING_PARTS.map(part => {
          const isActive = input.value === part && !showCustom
          return (
            <Button
              key={part}
              h="44px"
              px={4}
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? 'teal' : 'gray'}
              borderRadius={10}
              flex={1}
              minW={0}
              fontSize="sm"
              fontFamily="hero-new"
              fontWeight={isActive ? 600 : 400}
              onClick={() => {
                input.onChange(part)
                setShowCustom(false)
              }}>
              {part}
            </Button>
          )
        })}
        <Button
          h="44px"
          px={4}
          variant={showCustom ? 'solid' : 'outline'}
          colorScheme={showCustom ? 'blue' : 'gray'}
          borderRadius={10}
          fontSize="sm"
          fontWeight={showCustom ? 600 : 400}
          fontFamily="hero-new"
          onClick={() => {
            if (!showCustom) {
              input.onChange('')
            }
            setShowCustom(v => !v)
          }}>
          Other…
        </Button>
      </Flex>
      {showCustom ? (
        <Input
          value={input.value}
          onChange={e => input.onChange(e.target.value)}
          placeholder="Enter presenting part…"
          fontFamily="hero-new"
          fontSize="md"
          bg="white"
          _dark={{ bg: '#2a2a2a' }}
          borderRadius={10}
          h="44px"
          autoFocus
        />
      ) : null}
    </Flex>
  )
}

// ─── Bishop Score Display ─────────────────────────────────────────────────────

const BishopScoreBar = () => {
  const { input: dilInput } = useField<number>('dilation', { subscription: { value: true } })
  const { input: effInput } = useField<number>('effacement', { subscription: { value: true } })
  const { input: staInput } = useField<number>('station', { subscription: { value: true } })
  const { input: conInput } = useField<CervicalConsistency>('consistency', {
    subscription: { value: true },
  })
  const { input: posInput } = useField<CervicalPosition>('position', {
    subscription: { value: true },
  })

  const score = calcBishopScore(
    dilInput.value ?? 0,
    effInput.value ?? 0,
    staInput.value ?? 0,
    conInput.value ?? CervicalConsistency.FIRM,
    posInput.value ?? CervicalPosition.POSTERIOR,
  )

  const { text, color } = bishopLabel(score)
  const pct = (score / 13) * 100

  return (
    <Flex
      w="100%"
      flexFlow="column"
      gap={2}
      bg="white"
      _dark={{ bg: '#2a2a2a' }}
      borderRadius={14}
      border="1px solid"
      borderColor="gray.200"
      p={4}>
      <Flex justify="space-between" align="baseline">
        <Text
          fontSize="xs"
          fontFamily="hero-new"
          color="gray.500"
          _dark={{ color: 'gray.300' }}
          fontWeight={500}
          letterSpacing="wider">
          BISHOP SCORE
        </Text>
        <Flex align="baseline" gap={1.5}>
          <Text fontSize="2xl" fontWeight={700} fontFamily="hero-new" color={color} lineHeight={1}>
            {score}
          </Text>
          <Text fontSize="sm" color="gray.400" fontFamily="hero-new">
            / 13
          </Text>
          <Text fontSize="sm" fontWeight={600} fontFamily="hero-new" color={color} ml={1}>
            {text}
          </Text>
        </Flex>
      </Flex>
      <Box
        w="100%"
        h="8px"
        borderRadius="full"
        bg="gray.100"
        _dark={{ bg: 'gray.700' }}
        overflow="hidden">
        <Box
          h="100%"
          borderRadius="full"
          bg={color}
          w={`${pct}%`}
          transition="width 0.3s ease, background-color 0.3s ease"
        />
      </Box>
    </Flex>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const CervicalExamEntry = () => {
  const { input: posInput } = useField<CervicalPosition>('position', {
    subscription: { value: true },
  })
  const { input: conInput } = useField<CervicalConsistency>('consistency', {
    subscription: { value: true },
  })
  const { input: memInput } = useField<MembraneStatus>('membranes', {
    subscription: { value: true },
  })
  const topRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let el: HTMLElement | null = topRef.current
    while (el) {
      if (el.scrollHeight > el.clientHeight && getComputedStyle(el).overflowY !== 'visible') {
        el.scrollTop = 0
        break
      }
      el = el.parentElement
    }
  }, [])

  return (
    <Flex ref={topRef} justify="center" pb={8} w="100%">
      <Flex flexFlow="column" w="100%" maxW="520px" gap={5} py={4} px={2} align="center">
        {/* Bishop Score */}
        <BishopScoreBar />

        <Divider />

        {/* Dilation */}
        <DilationInput />

        <Divider />

        {/* Effacement */}
        <EffacementInput />

        <Divider />

        {/* Station */}
        <StationInput />

        <Divider />

        {/* Position */}
        <Flex flexFlow="column" w="100%" gap={2}>
          <SectionLabel>POSITION</SectionLabel>
          <SegmentedControl
            value={posInput.value ?? CervicalPosition.POSTERIOR}
            onChange={posInput.onChange}
            options={[
              { id: CervicalPosition.POSTERIOR, label: 'Posterior' },
              { id: CervicalPosition.MID_POSITION, label: 'Mid' },
              { id: CervicalPosition.ANTERIOR, label: 'Anterior' },
            ]}
          />
        </Flex>

        <Divider />

        {/* Consistency */}
        <Flex flexFlow="column" w="100%" gap={2}>
          <SectionLabel>CONSISTENCY</SectionLabel>
          <SegmentedControl
            value={conInput.value ?? CervicalConsistency.FIRM}
            onChange={conInput.onChange}
            options={[
              { id: CervicalConsistency.FIRM, label: 'Firm' },
              { id: CervicalConsistency.MEDIUM, label: 'Medium' },
              { id: CervicalConsistency.SOFT, label: 'Soft' },
            ]}
          />
        </Flex>

        <Divider />

        {/* Membranes */}
        <Flex flexFlow="column" w="100%" gap={2}>
          <SectionLabel>MEMBRANES</SectionLabel>
          <SegmentedControl
            value={memInput.value ?? MembraneStatus.INTACT}
            onChange={memInput.onChange}
            options={[
              { id: MembraneStatus.INTACT, label: 'Intact' },
              { id: MembraneStatus.SROM, label: 'SROM', sub: 'Spontaneous' },
              { id: MembraneStatus.AROM, label: 'AROM', sub: 'Artificial' },
            ]}
          />
        </Flex>

        <Divider />

        {/* Presenting Part */}
        <PresentingPartInput />
      </Flex>
    </Flex>
  )
}
